import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ucseAvailabilityService } from 'services';
import {
  BulkSmOrderAvailabilityModel,
  SmOrderAvailabilityPatchModel,
  QueryContextFromKeys,
  QueryParamModel,
  SmOrderAvailabilityPostModel,
} from 'models';

type UcseAvailabiltyContexts = QueryContextFromKeys<typeof availabilityKeys>;

export const availabilityKeys = {
  all: [{ scope: 'ucseAvailability' }] as const,
  lists: () => [{ ...availabilityKeys.all[0], entity: 'list' }] as const,
  list: (params?: QueryParamModel | null) => [{ ...availabilityKeys.lists()[0], params }] as const,
};

const fetchUcseAvailabiltyList = async ({
  queryKey: [{ params }],
}: UcseAvailabiltyContexts['list']) => ucseAvailabilityService.getUcseAvailabilityList(params);

const exportUcseAvailabilty = async ({ queryKey: [{ params }] }: UcseAvailabiltyContexts['list']) =>
  ucseAvailabilityService.exportUcseAvailability(params);

export const useUcseAvailabiltyList = (params?: QueryParamModel | null) =>
  useQuery({
    queryKey: availabilityKeys.list(params),
    queryFn: fetchUcseAvailabiltyList,
    enabled: !!params?.filter,
  });

export const useExportUcseAvailabilty = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: availabilityKeys.list(params),
    queryFn: exportUcseAvailabilty,
    enabled: false,
  });
};

export const usePatchUcseAvailabilty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: Partial<SmOrderAvailabilityPatchModel> }) =>
      ucseAvailabilityService.editUcseAvailability(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
    },
  });
};

export const usePostUcseAvailabilty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: Partial<SmOrderAvailabilityPostModel> }) =>
      ucseAvailabilityService.createUcseAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
    },
  });
};

export const useBulkUcseAvailabilty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: Partial<BulkSmOrderAvailabilityModel> }) =>
      ucseAvailabilityService.bulkUcseAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
    },
  });
};
